import type { ColumnFilterConfig } from '@/components/table/filter-chip';
import type { TableColumnDefinition, TableFilterDefinition } from '@/components/tables/lib';
import { useTranslation } from '@/hooks/use-translation';
import { $apiAllo } from '@/services/api-allo/client';
import { useMemo } from 'react';
import { AvailableOptionsValueInput } from './available-options-value-input';
import { DateValueInput } from './date-value-input';

/**
 * Maps API filter conditions (e.g., FROM, TO) to UI condition values and their server fields.
 * Activity columns have special handling (user options with avatars, translated activity types).
 * Non-activity columns use the column key as the server field and display labels from the backend.
 */
function mapApiConditionToCustomCondition(
  apiCondition: TableFilterDefinition['condition'],
  column: TableColumnDefinition,
): {
  value: string;
  labelKey: string;
  serverField: string;
  translateOptions?: boolean;
  isUserOption?: boolean;
} | null {
  if (column.type === 'ACTIVITY') {
    switch (apiCondition) {
      case 'FROM':
        return {
          value: 'from',
          labelKey: 'from',
          serverField: column.key,
          isUserOption: true,
        };
      case 'TO':
        return {
          value: 'to',
          labelKey: 'to',
          serverField: column.key,
          isUserOption: true,
        };
      case 'IS':
        return { value: 'is', labelKey: 'is', serverField: column.key, translateOptions: true };
      case 'HAS':
        return { value: 'has', labelKey: 'has', serverField: column.key, translateOptions: true };
      default:
        return null;
    }
  }

  // Non-activity columns with available options
  switch (apiCondition) {
    case 'IS':
    case 'EQUALS':
      return { value: 'is', labelKey: 'is', serverField: column.key };
    default:
      return null;
  }
}

/**
 * Builds ColumnFilterConfig objects for columns that have filters with available_options.
 * This enables generic rendering of API-driven filter options.
 */
export function useAvailableOptionsColumnConfigs(
  columns: TableColumnDefinition[] | undefined,
): Record<string, ColumnFilterConfig> {
  const { t } = useTranslation('desktop_ui');
  const { data: userData } = $apiAllo.useQuery('get', '/v1/user', {});
  const currentUserId = userData?.data?.id;
  const currentUserName = userData?.data?.name ?? undefined;

  return useMemo(() => {
    if (!columns) {
      return {};
    }

    const configs: Record<string, ColumnFilterConfig> = {};

    for (const column of columns) {
      if (!column.filters || column.filters.length === 0) {
        continue;
      }

      // Boolean columns get hardcoded True/False options
      if (column.type === 'BOOLEAN') {
        const booleanOptions = [
          { key: 'true', label: t('components.contacts.filter.boolean.true') },
          { key: 'false', label: t('components.contacts.filter.boolean.false') },
        ];

        configs[column.key] = {
          renderValueInput: ({ value, onChange, autoOpen }) => (
            <AvailableOptionsValueInput
              condition="is"
              value={value}
              onChange={onChange}
              autoOpen={autoOpen}
              options={booleanOptions}
              showUserIcon={false}
            />
          ),
        };
        continue;
      }

      // Date columns get a calendar picker input
      if (column.type === 'DATE') {
        configs[column.key] = {
          renderValueInput: ({ value, onChange, autoOpen }) => (
            <DateValueInput value={value} onChange={onChange} autoOpen={autoOpen} />
          ),
        };
        continue;
      }

      // Check if any filter has available_options
      const filtersWithOptions = column.filters.filter(
        (f) => f.available_options && f.available_options.length > 0,
      );

      if (filtersWithOptions.length === 0) {
        continue;
      }

      // Build custom conditions from API filters
      const customConditions: { value: string; labelKey: string; serverField: string }[] = [];
      const conditionNeedsTranslation = new Map<string, boolean>();
      const conditionIsUserOption = new Map<string, boolean>();

      for (const filter of filtersWithOptions) {
        const mapped = mapApiConditionToCustomCondition(filter.condition, column);
        if (mapped) {
          customConditions.push({
            value: mapped.value,
            labelKey: mapped.labelKey,
            serverField: mapped.serverField,
          });
          conditionNeedsTranslation.set(mapped.value, mapped.translateOptions ?? false);
          conditionIsUserOption.set(mapped.value, mapped.isUserOption ?? false);
        }
      }

      if (customConditions.length === 0) {
        continue;
      }

      // Create a map of condition value to available options (with translated labels for activity types)
      const conditionOptionsMap = new Map<
        string,
        Array<{ key: string; label?: string | null; extra?: { image_url?: string } | null }>
      >();

      for (const filter of filtersWithOptions) {
        const mapped = mapApiConditionToCustomCondition(filter.condition, column);
        if (mapped && filter.available_options) {
          // Translate labels for activity types (when translateOptions is true)
          // API may return lowercase keys, but translations use uppercase
          const options = mapped.translateOptions
            ? filter.available_options.map((opt) => ({
                key: opt.key,
                label: t(
                  `components.companies.filter.activity.types.${opt.key.toUpperCase()}` as never,
                ),
              }))
            : filter.available_options.map((opt) => ({
                key: opt.key,
                label: opt.label,
                extra: (opt as { extra?: { image_url?: string } | null }).extra,
              }));
          conditionOptionsMap.set(mapped.value, options);
        }
      }

      configs[column.key] = {
        customConditions,
        renderValueInput: ({ condition, value, onChange, autoOpen }) => {
          const options = conditionOptionsMap.get(condition) ?? [];
          const isUserOption = conditionIsUserOption.get(condition) ?? false;

          return (
            <AvailableOptionsValueInput
              condition={condition}
              value={value}
              onChange={onChange}
              autoOpen={autoOpen}
              options={options}
              currentUserId={isUserOption ? currentUserId : undefined}
              currentUserName={isUserOption ? currentUserName : undefined}
              showUserIcon={isUserOption}
            />
          );
        },
      };
    }

    return configs;
  }, [columns, currentUserId, currentUserName, t]);
}
